export interface ProductLabel {
  productCode: string
  displayLabel: string
  pdfLabel: string
}

let labelsCache: ProductLabel[] | null = null

export async function getProductLabels(): Promise<ProductLabel[]> {
  if (labelsCache) return labelsCache

  try {
    const res = await fetch('/api/products/labels')
    if (!res.ok) {
      console.warn(`Failed to fetch product labels: HTTP ${res.status}`)
      return []
    }
    const contentType = res.headers.get("content-type")
    if (!contentType || !contentType.includes("application/json")) {
      console.warn("Failed to fetch product labels: response is not JSON")
      return []
    }
    const data = await res.json()
    labelsCache = data.data || []
    return labelsCache || []
  } catch (error) {
    console.error('Failed to fetch product labels:', error)
    return []
  }
}

export function getDisplayLabel(productCode: string, fallback?: string): string {
  if (!labelsCache) return fallback || productCode
  const label = labelsCache.find((l) => l.productCode === productCode)
  return label?.displayLabel || fallback || productCode
}

export function getPdfLabel(productCode: string, fallback?: string): string {
  if (!labelsCache) return fallback || productCode
  const label = labelsCache.find((l) => l.productCode === productCode)
  return label?.pdfLabel || fallback || productCode
}

// Map existing product names to display labels (for Quote Summary modal)
export function getDisplayLabelFromName(productName: string): string {
  const nameToLabel: Record<string, string> = {
    'Micro SME — Abu Dhabi': 'Micro SME',
    'Micro SME — Dubai & NE': 'Micro SME',
    'SME Plans (26–200)': 'SME',
    'IPMI — Individual': 'Nova Global Individual',
    'IPMI — SME': 'Nova Global SME',
  }
  return nameToLabel[productName] || productName
}

export function getPlanDisplayLabel({
  coverageArea,
  category,
  memberCount = 1,
  productCode,
  productName,
}: {
  coverageArea?: string
  category?: string
  memberCount?: number
  productCode?: string
  productName?: string
}): string {
  // 1. Determine International vs Regional
  const isInternational =
    productCode?.startsWith("ipmi_") ||
    productName?.toLowerCase().includes("ipmi") ||
    coverageArea?.toLowerCase().includes("international") ||
    false

  // 2. Determine Individual vs SME
  const isSME =
    category === "sme" ||
    category === "corporate" ||
    productCode === "sme_26_200" ||
    productCode === "ipmi_sme"
  const isIndividual = !isSME

  // 3. Map
  if (isIndividual) {
    return isInternational ? "Nova Global Individual" : "Individual"
  } else {
    if (isInternational) {
      return "Nova Global SME"
    } else {
      return memberCount < 26 ? "Micro SME" : "SME"
    }
  }
}

export function getProductNameDisplayLabel(productName: string, isIndividual: boolean): string {
  if (!isIndividual) return productName;

  // For individual quotes, replace "Micro SME" with "Individual"
  return productName
    .replace("Micro SME —", "Individual —")
    .replace("Micro SME", "Individual")
    .replace("SME Plans (26–200)", "Individual")
    .replace("IPMI — SME", "IPMI — Individual");
}


